import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { formatApiError } from "@/lib/api";
import { IMG } from "@/lib/images";
import { LogoMark } from "@/components/Logo";
import { Label, Input, PrimaryButton } from "@/components/fields";

export default function AuthPage() {
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/account";

  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success(t("auth.welcomeBack"));
      } else {
        await register(form.name, form.email, form.password);
        toast.success(t("auth.accountCreated"));
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid grid-cols-1 lg:grid-cols-2" data-testid="auth-page">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMG.kiteSunset} alt="Kitesurfers at sunset" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-night/80 via-night/30 to-night/20" />
        <div className="absolute bottom-0 p-12 text-sand">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-4">{t("auth.sideCaption")}</p>
          <h2 className="font-serif text-4xl leading-tight max-w-md">{t("auth.sideTitle")}</h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 sm:px-8 py-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <LogoMark size={40} />
            <div>
              <p className="font-serif text-2xl text-ink leading-none">Villa <span className="italic text-terracotta">Paraiso</span></p>
              <p className="text-xs text-ink/50 mt-1">{t("auth.guestAccount")}</p>
            </div>
          </div>

          <div className="flex rounded-full bg-ink/5 p-1 mb-6" data-testid="auth-mode-toggle">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={m === "login" ? "auth-switch-to-login" : "auth-switch-to-register"}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors ${mode === m ? "bg-night text-sand" : "text-ink/60 hover:text-ink"}`}
              >
                {m === "login" ? t("auth.signIn") : t("auth.createAccount")}
              </button>
            ))}
          </div>

          <button
            type="button"
            data-testid="auth-google-btn"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/account";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full rounded-full border border-ink/15 bg-white py-3 text-sm font-semibold text-ink hover:border-terracotta hover:text-terracotta transition-colors inline-flex items-center justify-center gap-2.5 mb-5"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.8z"/>
              <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.1 1.2-3.2 0-5.9-2.1-6.8-5l-3.9 3C3.3 21.3 7.3 24 12 24z"/>
              <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-3.9-3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.4l3.9-3z"/>
              <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.6l3.9 3c.9-2.9 3.6-4.9 6.8-4.9z"/>
            </svg>
            {t("auth.google")}
          </button>
          <div className="flex items-center gap-3 mb-6" data-testid="auth-divider">
            <span className="grow border-t border-ink/10" />
            <span className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/40">{t("auth.orDivider")}</span>
            <span className="grow border-t border-ink/10" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">            {mode === "register" && (
              <div>
                <Label htmlFor="auth-name">{t("auth.name")}</Label>
                <Input id="auth-name" data-testid="auth-name-input" required value={form.name} onChange={set("name")} placeholder={t("auth.namePh")} />
              </div>
            )}
            <div>
              <Label htmlFor="auth-email">{t("auth.email")}</Label>
              <Input id="auth-email" data-testid="auth-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>
            <div>
              <Label htmlFor="auth-password">{t("auth.password")}</Label>
              <Input id="auth-password" data-testid="auth-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder={t("auth.passwordPh")} />
            </div>
            {error && (
              <p className="text-sm text-destructive bg-destructive/10 rounded-xl px-4 py-3" data-testid="auth-error">{error}</p>
            )}
            <PrimaryButton data-testid="auth-submit-btn" type="submit" disabled={busy} className="w-full">
              {mode === "login" ? <LogIn size={16} /> : <UserPlus size={16} />}
              {busy ? t("auth.wait") : mode === "login" ? t("auth.submitLogin") : t("auth.submitRegister")}
            </PrimaryButton>
          </form>

          <p className="text-xs text-ink/45 mt-6 text-center leading-relaxed">{t("auth.ownerNote")}</p>
          <p className="text-xs text-ink/45 mt-2 text-center">
            <Link to="/" data-testid="auth-back-home" className="text-terracotta font-semibold hover:underline">{t("auth.backHome")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
